import React from 'react';
import { Mic } from 'lucide-react';
import RecordingTimer from '../../../ai-voices/voice-cloning/RecordingTimer';
import MicDevicePicker from '../../MicDevicePicker';
import { MicDevice } from '../../useAudioInputController';

interface BootstrapRecordingPanelProps {
    onBack: () => void;
    isRecording: boolean;
    isStartingRecording: boolean;
    countdown: number | null;
    recordingDuration: number;
    maxRecordingDurationSec: number;
    minRecordingDurationSec: number;
    showMicPicker: boolean;
    micDevices: MicDevice[];
    selectedMicDeviceId: string;
    onSelectMicDevice: (deviceId: string) => void;
    onStartCountdown: () => void;
    onStop: () => void;
    /** Denser padding/sizing for narrow widgets/sidebars. Default false. */
    compact?: boolean;
}

// Mic picker + countdown + active-recording states.
export default function BootstrapRecordingPanel({
    onBack, isRecording, isStartingRecording, countdown, recordingDuration, maxRecordingDurationSec, minRecordingDurationSec,
    showMicPicker, micDevices, selectedMicDeviceId, onSelectMicDevice, onStartCountdown, onStop,
    compact = false,
}: BootstrapRecordingPanelProps) {
    const canStart = !showMicPicker || !!selectedMicDeviceId;
    // Only allow stopping once the minimum has been captured; until then the recording must continue.
    const canStop = recordingDuration >= minRecordingDurationSec;

    return (
        <div
            className="mt-3"
            style={{
                border: '2px dashed rgba(var(--bs-primary-rgb), 0.25)',
                borderRadius: compact ? '12px' : '16px',
                padding: compact ? '14px' : '24px',
                background: 'rgba(var(--bs-primary-rgb), 0.02)',
            }}
        >
            <style>{`
                @keyframes audioInputCountdownScaleIn {
                    0% { transform: scale(0.5); opacity: 0; }
                    50% { transform: scale(1.1); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .audio-input-countdown-number { animation: audioInputCountdownScaleIn 0.5s ease-out; }
            `}</style>

            <div className={compact ? 'mb-2' : 'mb-4'}>
                <button
                    onClick={onBack}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: compact ? '11px' : '13px',
                        color: '#90a0b7',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        transition: 'color 0.18s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--bs-primary)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#90a0b7'; }}
                >
                    ← Back to Upload
                </button>
            </div>

            {countdown === null && !isRecording && !isStartingRecording && (
                <div className={`d-flex justify-content-center align-items-center flex-wrap ${compact ? 'gap-2' : 'gap-3'}`}>
                    {showMicPicker && (
                        <MicDevicePicker
                            devices={micDevices}
                            selectedDeviceId={selectedMicDeviceId}
                            onSelect={onSelectMicDevice}
                        />
                    )}

                    <button
                        onClick={onStartCountdown}
                        disabled={!canStart}
                        style={{
                            background: canStart
                                ? 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)'
                                : 'rgba(var(--bs-primary-rgb), 0.06)',
                            color: canStart ? 'white' : '#90a0b7',
                            border: 'none',
                            borderRadius: compact ? '8px' : '10px',
                            padding: compact ? '7px 16px' : '10px 22px',
                            fontSize: compact ? '12px' : '13px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            cursor: canStart ? 'pointer' : 'not-allowed',
                            boxShadow: canStart ? '0 4px 14px rgba(var(--bs-primary-rgb), 0.28)' : 'none',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <Mic size={compact ? 12 : 14} />
                        Start Recording
                    </button>
                </div>
            )}

            {countdown !== null && (
                <div style={{ textAlign: 'center', padding: compact ? '16px 10px' : '48px 20px' }}>
                    <div
                        className="audio-input-countdown-number"
                        key={countdown}
                        style={{
                            fontSize: compact ? '48px' : '110px',
                            fontWeight: '800',
                            background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            lineHeight: '1',
                        }}
                    >
                        {countdown}
                    </div>
                    <p style={{ fontSize: compact ? '11px' : '14px', color: '#90a0b7', marginTop: compact ? 6 : 16, fontWeight: 500 }}>
                        Get ready to record...
                    </p>
                </div>
            )}

            {(isRecording || isStartingRecording) && (
                <div style={{ textAlign: 'center', padding: compact ? '12px 10px' : '40px 20px' }}>
                    <div style={{ marginBottom: compact ? 12 : 32, transform: compact ? 'scale(0.7)' : undefined }}>
                        <RecordingTimer duration={recordingDuration} maxDuration={maxRecordingDurationSec} />
                    </div>
                    {canStop ? (
                        <>
                            <button
                                onClick={onStop}
                                aria-label="Stop recording"
                                title="Stop recording"
                                style={{
                                    width: compact ? 40 : 64,
                                    height: compact ? 40 : 64,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 6px 20px rgba(245, 87, 108, 0.35)',
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                            >
                                <div style={{ width: compact ? 14 : 20, height: compact ? 14 : 20, backgroundColor: 'white', borderRadius: 4 }} />
                            </button>
                            <p style={{ fontSize: compact ? '11px' : '12px', color: '#90a0b7', marginTop: compact ? 8 : 12 }}>
                                Click to stop recording — we recommend up to {maxRecordingDurationSec}s for best results.
                            </p>
                        </>
                    ) : (
                        <p style={{ fontSize: compact ? '11px' : '12px', color: '#90a0b7', marginTop: compact ? 8 : 12 }}>
                            Keep speaking — a minimum of {minRecordingDurationSec}s is required.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
